<template>
  <div class="ma-0">
    <div v-if="hasAgencySetting('TAB_PROSPECTS') && applicationStore.user.internal">
      <gat-grid
        title="Prospects"
        class="elevation-0"
        :items="prospectItems"
        :columns="columns"
        reloadBtn
        :fixedheader="true"
        height="calc(100vh - 135px)"
        :hideEmptyColumns="true"
        viewGroupName="prospectsGrid"
        @reload-clicked="getProspectLines(callId)"
        @row-clicked="onRowClicked"
        selectable
        keyName="CHO_ID"
        @selectionChanged="prospectsGridSelectionChanged($event)">
        <template v-slot:cell="cellProps">
          <div
            v-if="
              cellProps.column.field == 'Client' || cellProps.column.field == 'Cargo' || cellProps.column.field == 'Sof'
            "
            class="d-flex justify-start align-center">
            <span>
              {{
                formatProspectSemicolonString(cellProps.value).value.length >= 50
                  ? formatProspectSemicolonString(cellProps.value).value.substring(0, 50) + '...'
                  : formatProspectSemicolonString(cellProps.value).value
              }}
            </span>
            <div
              v-if="
                formatProspectSemicolonString(cellProps.value).values &&
                formatProspectSemicolonString(cellProps.value).values.length >= 1
              "
              @click.stop>
              <v-tooltip
                bottom
                v-if="
                  formatProspectSemicolonString(cellProps.value).values &&
                  formatProspectSemicolonString(cellProps.value).values.length >= 1
                ">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" class="ml-1">
                    <v-chip x-small color="blue" class="pa-1 white--text" label>
                      +{{ formatProspectSemicolonString(cellProps.value).values.length }}
                    </v-chip>
                  </div>
                </template>
                <div v-for="people in formatProspectSemicolonString(cellProps.value).values" :key="people">
                  <span>{{ people }}</span>
                </div>
              </v-tooltip>
            </div>
          </div>
        </template>
        <template slot="btns">
          <wm-button-group
            class="right ml-auto"
            :buttons="wmButtonGroup()"
            @createEmailClick="createEmailClick"
            @click="buttonGroupClicked"
            @menuItemClick="buttonGroupClicked"
            @subMenuItemClick="buttonGroupClicked" />
        </template>
      </gat-grid>
      <email-generator
        ref="emailGenerator"
        :portcallId="callId"
        location="portcall"
        @isLoadingOverlay="isSaving = $event" />
    </div>
    <div v-else>You don't have access to prospects!</div>
    <gat-dialog
      v-model="deleteProspecstDialog"
      cancelButtonCaption="CANCEL"
      okButtonCaption="CONFIRM"
      :cancel-button-disabled="isSaving"
      :ok-button-disabled="isSaving || !canDeleteProspects"
      width="auto"
      :auto-close="false"
      @okClick="deleteSelectedProspects()"
      @cancelClick="deleteProspecstDialog = false">
      <span class="text-h6 grey--text text--darken-1">Delete selected prospects</span>
      <div>
        <span>Are you sure you want to delete these prospects? This process cannot be undone.</span>
      </div>
    </gat-dialog>
    <prospect-item
      v-if="prospectDialogVisible && callId"
      :show-form="prospectDialogVisible"
      @close="closeProspectDialog($event)"
      :call-id="callId"
      :is-new-prospect="true"
      :clients-contacts="clientsContacts"
      :selected-prospect="selectedProspect" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
// eslint-disable-next-line import/no-cycle
import { apiGet, apiPost, apiDelete } from '@/store/api';
import EmailGenerator from '@/app-components/EmailGenerator.vue';
import GatUtils from '@/app-components/GatUtils';
import { usePortcallStore } from '@/store/portcallStore';
import { useAppStatusStore } from '@/store/appStatusStore';
import { useApplicationStore } from '@/store/applicationStore';
import { useHelperStore } from '@/store/helperStore';
import { getCargoItems } from '@/services/api/api-calls/getCargoItems';
import { getHotlist } from '@/services/api/api-calls/getHotlist';
import ProspectItem from './ProspectItem.vue';

type ProspectClientRow = {
  ID: any;
  ROLE: any;
  NAME: any;
  EMAIL: any;
  RowType: number;
  CLIENT_ROLE_ID: any;
  SORTORDER: number | null;
  data: any;
};

export default Vue.extend({
  name: 'ProspectsList',
  props: {
    callId: { type: [String, Number], required: true },
  },
  components: { EmailGenerator, GatUtils, ProspectItem },
  setup() {
    const portcallStore = usePortcallStore();
    const appStatusStore = useAppStatusStore();
    const applicationStore = useApplicationStore();
    const helperStore = useHelperStore();
    return {
      applicationStore,
      portcallStore,
      appStatusStore,
      helperStore,
    };
  },
  data() {
    return {
      isSaving: false,
      selectedIds: [],
      emailTemplates: [],
      selectedProspect: null,
      prospectItems: [] as any[],
      prospectDialogVisible: false,
      deleteProspecstDialog: false,
      clientsContacts: [] as ProspectClientRow[],
      clientsContactsRevision: 0,
    };
  },

  computed: {
    callDetails() {
      return this.portcallStore.callDetails;
    },
    hotlist() {
      return this.portcallStore.hotlist;
    },
    canCreateNewProspect() {
      return this.applicationStore.userRight('PRO.NEW') && this.applicationStore.user.internal;
    },
    canEditProspect() {
      return this.applicationStore.userRight('PRO.EDIT') && this.applicationStore.user.internal;
    },
    canDeleteProspects() {
      return this.applicationStore.userRight('PRO.DELETE') && this.applicationStore.user.internal;
    },
    columns() {
      const result = [];
      result.push({ header: 'Name', field: 'CHO_NAME', hideOnPhone: false });
      result.push({ header: 'Subject', field: 'CHO_SUBJECT', hideOnPhone: false });
      result.push({ header: 'Updated', field: 'Updated', hideOnPhone: false });
      result.push({ header: 'Updated by', field: 'CHO_LAST_UPDATED_BY', hideOnPhone: false });
      result.push({ header: 'Client', field: 'Client', hideOnPhone: true });
      result.push({ header: 'Cargo', field: 'Cargo', hideOnPhone: true });
      result.push({ header: 'SOF', field: 'Sof', hideOnPhone: true });
      return result;
    },

    emailTemplateItems() {
      const templates = [...this.emailTemplates];
      let result: any[] = [];
      const ignoreGroupMinCount = this.applicationStore.hasFeatureCode('IGNORE_DOCUMENT_GROUPING_MIN_COUNT');

      if (Array.isArray(templates) && templates.length > 0) {
        if (ignoreGroupMinCount ? true : templates.length > 10) {
          result = this.helperStore.emailTemplatesGroupedByLabels(templates, false);
        } else {
          result = templates.map((item: any) => ({
            caption: item.TITLE,
            eventName: 'createEmailClick',
            icon: 'mdi-file-outline',
            ID: item.ID,
          }));
        }
      }

      return result;
    },

    applicationStatus() {
      return this.appStatusStore.getAppStatus;
    },
  },

  activated() {
    this.isSaving = false;
    this.prospectDialogVisible = false;
    if (this.callId) this.getProspectLines(this.callId);
  },

  created() {},

  watch: {
    callId: {
      handler(val) {
        this.getProspectLines(val);
        getCargoItems(val);
        getHotlist(val);
      },
      immediate: true,
    },
    hotlist: {
      async handler(clients) {
        const revision = ++this.clientsContactsRevision;
        const connectedClients = (clients ?? [])
          .map((client: any) => {
            let rowType = 1;
            if (client.H_ID == -1) rowType = 3;
            else if (client.H_ID == -2) rowType = 4;
            return { ...client, RowType: Number(client.RowType ?? rowType) };
          })
          // company rows have no email requirement.
          .filter(
            (client: any) =>
              client.CLIENT_ID > 0 && (client.RowType === 3 || (client.RowType === 1 && client.CLIENT_ROLE_ID > 0))
          );
        const companyRequests = new Map<any, Promise<{ CONTACT_LIST: any[]; CLIENT_NAME: any }>>();
        connectedClients.forEach((client: any) => {
          // Provisional IDs belong to Hotlist_temporary; fetch each company's contacts once across roles.
          if (client.RowType === 1 && !companyRequests.has(client.CLIENT_ID)) {
            companyRequests.set(
              client.CLIENT_ID,
              apiPost('contacts/', {
                maxRows: 200,
                searchStr: '',
                id: -1,
                category: -1,
                clientId: client.CLIENT_ID,
                includeInactive: true,
              })
                .then(async (contacts) => {
                  const company = contacts[0]?.C_NAME == null ? await apiGet(`clients/${client.CLIENT_ID}`) : null;
                  return {
                    CONTACT_LIST: contacts,
                    CLIENT_NAME: contacts[0]?.C_NAME ?? company?.NAME ?? client.C_NAME,
                  };
                })
                .catch(() => ({ CONTACT_LIST: [], CLIENT_NAME: client.C_NAME }))
            );
          }
        });

        const dataset: ProspectClientRow[] = [];
        await Promise.all(
          connectedClients.map(async (d: any) => {
            const company = d.RowType === 1 ? await companyRequests.get(d.CLIENT_ID) : undefined;
            dataset.push({
              ID: d.CLIENT_ID,
              ROLE: d.CT_NAME,
              NAME: company?.CLIENT_NAME ?? d.C_NAME,
              EMAIL: d.C_EMAIL,
              RowType: d.RowType,
              CLIENT_ROLE_ID: d.CLIENT_ROLE_ID,
              SORTORDER: d.RowType === 3 ? 10000 : d.SORTORDER,
              data: d,
            });
            if (d.RowType === 3) return;
            company?.CONTACT_LIST.forEach((c: any) => {
              dataset.push({
                ID: c.ID,
                ROLE: d.CT_NAME,
                NAME: `${company.CLIENT_NAME} - ${c.LAST_NAME ?? ''}${c.FIRST_NAME == null ? '' : `, ${c.FIRST_NAME}`}`,
                EMAIL: c.EMAIL,
                RowType: 2,
                CLIENT_ROLE_ID: d.CLIENT_ROLE_ID,
                SORTORDER: d.SORTORDER,
                data: c,
              });
            });
          })
        );

        if (revision !== this.clientsContactsRevision) return;

        dataset.sort(
          (a, b) =>
            (a.SORTORDER ?? Number.MIN_SAFE_INTEGER) - (b.SORTORDER ?? Number.MIN_SAFE_INTEGER) ||
            (a.ROLE ?? '').localeCompare(b.ROLE ?? '') ||
            (a.NAME ?? '').localeCompare(b.NAME ?? '')
        );
        this.clientsContacts = dataset;
      },
      immediate: true,
    },
  },

  methods: {
    wmButtonGroup() {
      const result = [];
      result.push({
        btnType: 'ADD',
        docType: 19,
        eventName: 'addNewProspectClicked',
        agencyId: this.callDetails.SETUP_ID,
        applicationStatus: this.applicationStatus,
        location: 'portcall',
        foreignKey: this.callId,
        disabled: !this.canCreateNewProspect,
      });
      result.push({
        btnType: 'CUSTOM',
        icon: 'mdi-circle-edit-outline',
        caption: 'EDIT',
        eventName: 'editSelectedProspectClicked',
        docType: 19,
        agencyId: this.callDetails.SETUP_ID,
        applicationStatus: this.applicationStatus,
        location: 'portcall',
        foreignKey: this.callId,
        disabled: this.selectedIds.length !== 1 || !this.canEditProspect,
      });
      result.push({
        btnType: 'DELETE',
        eventName: 'deleteSelectedProspectsClicked',
        docType: 19,
        agencyId: this.callDetails.SETUP_ID,
        applicationStatus: this.applicationStatus,
        location: 'portcall',
        foreignKey: this.callId,
        disabled: this.selectedIds.length < 1 || !this.canDeleteProspects,
      });
      result.push({
        btnType: 'CREATE_DOC',
        docType: 19,
        agencyId: this.callDetails.SETUP_ID,
        applicationStatus: this.applicationStatus,
        location: 'portcall',
        foreignKey: this.callId,
        disabled: this.selectedIds.length !== 1,
      });
      result.push({
        btnType: 'CUSTOM',
        caption: 'Create email',
        icon: 'mdi-email',
        menuItems: this.emailTemplateItems,
        disabled: this.selectedIds.length !== 1 || this.emailTemplateItems.length === 0,
      });
      return result;
    },
    hasAgencySetting(value: string) {
      value.trim();
      let result = 0;
      const portcallAgency = this.callDetails.SETUP_ID; // Agency to which the current portcall belongs.
      if (portcallAgency) {
        const idx = this.applicationStore.agencySettings.findIndex((item: any) => item.ID == portcallAgency); // Find agencySettings for portcallAgency
        if (idx >= 0) {
          const agency = this.applicationStore.agencySettings[idx]; // Store all the agency settings.
          result = agency[value]; // Check the agency setting for the given argument.
        }
      }
      return result == 1;
    },
    getProspectLines(portCallId: string | number) {
      this.prospectItems = [];
      this.emailTemplates = [];

      apiGet(`prospects/${portCallId}`).then((response: any) => {
        this.prospectItems = [...response];
        this.portcallStore.setTabHasData({ tabName: 'PROSPECTS', value: response.length });
      });

      apiGet(`mail/templates/prospects/${this.callDetails.SETUP_ID}/${true}`).then((result: any) => {
        if (result) {
          this.emailTemplates = result;
        }
      });
    },
    onRowClicked(item: any) {
      if (item && item.CHO_ID) this.$router.push({ name: 'prospectDetails', params: { prospectId: item.CHO_ID } });
    },
    prospectsGridSelectionChanged(item: any) {
      this.appStatusStore.setAppStatusSelectedProspectId(item[0]);
      this.selectedIds = item;
    },
    buttonGroupClicked(btn: any) {
      const event = btn.button.eventName;
      if (event === 'addNewProspectClicked') {
        this.selectedProspect = null;
        this.prospectDialogVisible = true;
      } else if (event === 'editSelectedProspectClicked') {
        if (this.selectedIds.length === 1) {
          this.selectedProspect = this.prospectItems.find((item) => item.CHO_ID == this.selectedIds[0]);
          this.prospectDialogVisible = true;
        }
      } else if (event === 'deleteSelectedProspectsClicked') {
        this.deleteProspecstDialog = true;
      }
    },
    closeProspectDialog(cancelled: boolean) {
      this.prospectDialogVisible = false;
      if (this.callId && !cancelled) {
        this.getProspectLines(this.callId);
      }
    },
    async deleteSelectedProspects() {
      try {
        this.isSaving = true;
        const promises: any[] = [];
        this.selectedIds.forEach((selectedId) => {
          promises.push(apiDelete(`/prospects/${selectedId}`));
        });
        await Promise.allSettled(promises);
        this.selectedIds = [];
        this.getProspectLines(this.callId);
        this.isSaving = false;
        this.deleteProspecstDialog = false;
      } catch {
        this.isSaving = false;
      } finally {
        this.isSaving = false;
      }
    },
    async createEmailClick(item: any) {
      const template = this.emailTemplates.find((entry: any) => entry.ID === item.ID);
      if (this.callId && template) {
        await (
          this.$refs.emailGenerator as Vue & {
            generateEmail: (value: any, applicationStatus: object) => Promise<void>;
          }
        ).generateEmail(template, this.applicationStatus);
      }
    },

    formatProspectSemicolonString(item: any) {
      let values = null;
      let value = item;
      if (item) {
        values = item.split(';');
        if (typeof values == 'object' && values.length >= 2) {
          // eslint-disable-next-line prefer-destructuring
          value = values[0];
        }
        values.splice(0, 1);
      }
      return { value, values };
    },
  },
});
</script>

<style scoped></style>
